import { useEffect, useSyncExternalStore } from 'react';

/**
 * Estado de sesión/carga compartido por toda la SPA (sin Provider: evita envolver 24 rutas en Marketplace).
 * `useMarketplaceHome` publica aquí; cualquier pantalla puede leerlo con `useAppSession`.
 */
const DEFAULT_SNAP = Object.freeze({
  authReady: false,
  dataRefreshKey: 0,
  user: null,
});

let snap = { ...DEFAULT_SNAP };
const listeners = new Set();

function emit() {
  listeners.forEach(fn => {
    try {
      fn();
    } catch {
      /* ignore subscriber errors */
    }
  });
}

export function publishAppSession(partial) {
  if (!partial || typeof partial !== 'object') return;
  let changed = false;
  const next = { ...snap };
  for (const [k, v] of Object.entries(partial)) {
    if (next[k] !== v) {
      next[k] = v;
      changed = true;
    }
  }
  if (!changed) return;
  snap = next;
  emit();
}

export function getAppSessionSnapshot() {
  return snap;
}

function subscribeAppSession(onStoreChange) {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
}

/** @returns {{ authReady: boolean, dataRefreshKey: number, user: import('@supabase/supabase-js').User | null }} */
export function useAppSession() {
  return useSyncExternalStore(subscribeAppSession, getAppSessionSnapshot, getAppSessionSnapshot);
}

/**
 * Ejecuta `effect` cuando la sesión está lista y tras cada reconexión global (`dataRefreshKey`).
 * @param {() => void | (() => void)} effect
 * @param {unknown[]} deps
 */
export function useAppReconnectEffect(effect, deps = []) {
  const { authReady, dataRefreshKey } = useAppSession();
  useEffect(() => {
    if (!authReady) return undefined;
    return effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataRefreshKey es señal global de reconexión
  }, [authReady, dataRefreshKey, ...deps]);
}
